import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Scale, FileText, Shield, Lock, Globe, AlertTriangle,
  RefreshCw, Mail, ChevronRight, CheckCircle, Ban, Copyright,
  ExternalLink, Users, MessageSquare, Phone, Building2, Gavel
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TermsPage = () => {
  const [activeSection, setActiveSection] = useState('');

  // Table of contents sections
  const sections = [
    { id: 'business-description', title: '1. Business Description', icon: Building2 },
    { id: 'sms-terms', title: '2. SMS Messaging Terms', icon: MessageSquare },
    { id: 'general-terms', title: '3. General Terms', icon: FileText },
    { id: 'user-eligibility', title: '4. User Eligibility', icon: Users },
    { id: 'intellectual-property', title: '5. Intellectual Property', icon: Copyright },
    { id: 'disclaimers', title: '6. Disclaimers', icon: AlertTriangle },
    { id: 'third-party-links', title: '7. Third-Party Links', icon: ExternalLink },
    { id: 'online-commerce', title: '8. Online Commerce', icon: Globe },
    { id: 'account-security', title: '9. Account & Security', icon: Lock },
    { id: 'termination', title: '10. Termination', icon: Ban },
    { id: 'governing-law', title: '11. Governing Law', icon: Gavel },
    { id: 'changes', title: '12. Changes to Terms', icon: RefreshCw },
    { id: 'contact', title: '13. Contact Information', icon: Phone }
  ];

  // Track scroll position for active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;
      
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  // Scroll to section
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 120;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Terms of Service | ASAP Capital</title>
        <meta 
          name="description" 
          content="Review the terms of service governing the use of the ASAP Capital website, including SMS messaging terms, legal rights, limitations, and responsibilities." 
        />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://asapcapital.net/terms" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section className="relative pt-36 pb-12 lg:pt-44 lg:pb-16 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]" />
            
            <div className="relative z-10 max-w-[900px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 bg-white/5 border border-white/10">
                  <Scale className="w-4 h-4 text-white/60" />
                  <span className="text-xs font-medium text-white/60 uppercase tracking-wider">Legal</span>
                </div>

                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.2] mb-6">
                  Terms of Service
                </h1>

                <div className="text-white/60 text-base lg:text-lg leading-relaxed space-y-4">
                  <p>
                    These Terms of Service ("Terms") govern your access to and use of the website and services provided by ASAP Capital ("Company," "we," "us," or "our"). By accessing or using our website (the "Website") or services, you agree to be bound by these Terms and our{' '}
                    <a 
                      href="https://asapcapital.net/privacy" 
                      className="text-[#D4AF37] hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>.
                  </p>
                  <p className="font-medium text-white/80">
                    If you do not agree with these Terms, please do not use our Website or services.
                  </p>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== MAIN CONTENT WITH TOC ==================== */}
          <section className="py-12 lg:py-16 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <div className="grid lg:grid-cols-[280px_1fr] gap-12">
                
                {/* Sticky Table of Contents - Desktop Only */}
                <div className="hidden lg:block">
                  <div className="sticky top-32">
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-4">On This Page</p>
                    <nav className="space-y-1">
                      {sections.map((section) => (
                        <button
                          key={section.id}
                          onClick={() => scrollToSection(section.id)}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm transition-all ${
                            activeSection === section.id
                              ? 'bg-white/10 text-white border-l-2 border-[#D4AF37]'
                              : 'text-white/50 hover:text-white/70 hover:bg-white/5'
                          }`}
                        >
                          <section.icon className="h-4 w-4 flex-shrink-0" />
                          <span className="line-clamp-1">{section.title}</span>
                        </button>
                      ))}
                    </nav>

                    <div className="mt-8 pt-6 border-t border-white/10">
                      <p className="text-white/40 text-xs uppercase tracking-wider mb-3">Related Pages</p>
                      <div className="space-y-2">
                        <a 
                          href="https://asapcapital.net/privacy"
                          className="flex items-center gap-2 text-white/50 hover:text-[#D4AF37] text-sm transition-colors"
                        >
                          <ChevronRight className="h-3 w-3" />
                          Privacy Policy
                        </a>
                        <Link 
                          to="/disclosures"
                          className="flex items-center gap-2 text-white/50 hover:text-[#D4AF37] text-sm transition-colors"
                        >
                          <ChevronRight className="h-3 w-3" />
                          Disclosures
                        </Link>
                        <Link 
                          to="/contact"
                          className="flex items-center gap-2 text-white/50 hover:text-[#D4AF37] text-sm transition-colors"
                        >
                          <ChevronRight className="h-3 w-3" />
                          Contact Us
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Main Content */}
                <div className="max-w-[700px]">
                  
                  {/* 1. Business Description */}
                  <section id="business-description" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <Building2 className="h-6 w-6 text-[#D4AF37]" />
                        1. Business Description
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          ASAP Capital provides real estate private equity investment services, educational resources, consultation services, and related communications to investors and prospective investors.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* 2. SMS Messaging Terms & Conditions */}
                  <section id="sms-terms" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <MessageSquare className="h-6 w-6 text-[#D4AF37]" />
                        2. SMS Messaging Terms & Conditions
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-6">
                        <p>
                          By providing your phone number and submitting a form through our Website or approved lead forms, you consent to receive SMS (text) messages from ASAP Capital in accordance with your selected communication preferences.
                        </p>

                        {/* Business Identity & Program Description */}
                        <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                          <h3 className="text-lg font-semibold text-white mb-3">Business Identity & Program Description</h3>
                          <p className="mb-4">
                            ASAP Capital is a real estate private equity investment firm that provides consultation services, investment opportunity updates, and related communications.
                          </p>
                          <p className="mb-3">If you opt in to receive SMS messages, you may receive:</p>
                          <ul className="list-disc list-inside space-y-1 ml-2">
                            <li>Inquiry responses</li>
                            <li>Consultation scheduling and confirmations</li>
                            <li>Appointment reminders</li>
                            <li>Service-related updates</li>
                            <li>Educational messages</li>
                            <li>Investment opportunity notifications</li>
                            <li>Event or webinar announcements</li>
                          </ul>
                          <p className="mt-4 text-white/50 text-sm">
                            Messages are sent only to users who have provided explicit consent through our website forms or approved opt-in channels.
                          </p>
                        </div>

                        {/* Opt-Out & Support Mechanisms */}
                        <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                          <h3 className="text-lg font-semibold text-white mb-3">Opt-Out & Support Mechanisms</h3>
                          <p className="mb-3">
                            You can cancel the SMS service at any time. Just text <strong className="text-white">"STOP"</strong> to{' '}
                            <a href="tel:+13463100995" className="text-[#D4AF37] hover:underline">+1 (346) 310-0995</a>.
                          </p>
                          <p className="mb-3">
                            After you send the SMS message "STOP" to us, we will send you an SMS message to confirm that you have been unsubscribed. After this, you will no longer receive SMS messages from us. If you want to join again, just sign up as you did the first time and we will start sending SMS messages to you again.
                          </p>
                          <p className="mb-3">
                            If you are experiencing issues with the messaging program, you can reply with the keyword <strong className="text-white">"HELP"</strong> for assistance, or you can contact us directly at:
                          </p>
                          <div className="flex flex-col gap-2 mt-3">
                            <p>
                              <span className="text-white/40">Email:</span>{' '}
                              <a href="mailto:info@asapcapital.net" className="text-[#D4AF37] hover:underline">info@asapcapital.net</a>
                            </p>
                            <p>
                              <span className="text-white/40">Phone:</span>{' '}
                              <a href="tel:+13463100995" className="text-[#D4AF37] hover:underline">+1 (346) 310-0995</a>
                            </p>
                          </div>
                        </div>

                        {/* Carrier Liability Clause */}
                        <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                          <h3 className="text-lg font-semibold text-white mb-3">Carrier Liability Clause</h3>
                          <p>
                            Carriers are not liable for delayed or undelivered messages.
                          </p>
                        </div>

                        {/* Message Frequency & Data Rates */}
                        <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                          <h3 className="text-lg font-semibold text-white mb-3">Message Frequency & Data Rates</h3>
                          <p className="mb-3">
                            As always, message and data rates may apply for any messages sent to you from us and to us from you. You will receive message frequency that varies based on your interaction with our services (for example, up to 6 messages per month, plus replies to your requests). If you have any questions about your text plan or data plan, it is best to contact your wireless provider.
                          </p>
                        </div>

                        {/* Privacy Policy Reference */}
                        <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                          <h3 className="text-lg font-semibold text-white mb-3">Privacy Policy Reference</h3>
                          <p className="mb-3">
                            If you have any questions regarding privacy, please review our Privacy Policy at:{' '}
                            <a 
                              href="https://asapcapital.net/privacy" 
                              className="text-[#D4AF37] hover:underline"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              https://asapcapital.net/privacy
                            </a>
                          </p>
                          <p className="text-white/50 text-sm">
                            No mobile information will be shared with third parties/affiliates for marketing/promotional purposes. Information sharing to subcontractors in support services, such as customer service, is permitted. All other use case categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.
                          </p>
                        </div>

                        {/* Compliance Statement */}
                        <div className="bg-[#D4AF37]/10 rounded-xl p-5 border border-[#D4AF37]/20">
                          <h3 className="text-lg font-semibold text-white mb-3">Compliance Statement</h3>
                          <p>
                            ASAP Capital complies with all applicable federal and state laws and regulations, including the Telephone Consumer Protection Act (TCPA) and CTIA messaging guidelines, regarding the use of SMS communications.
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  </section>

                  {/* 3. General Terms */}
                  <section id="general-terms" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <FileText className="h-6 w-6 text-[#D4AF37]" />
                        3. General Terms
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          This website (the "Site") is owned and operated by ASAP Capital ("Company," "we," "us," or "our").
                        </p>
                        <p>
                          By using the Site, you agree to be bound by these Terms of Service and to use the Site in accordance with these Terms of Service, our{' '}
                          <a 
                            href="https://asapcapital.net/privacy" 
                            className="text-[#D4AF37] hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Privacy Policy
                          </a>, and any additional terms and conditions that may apply to specific sections of the Site or to products and services available through the Site or from ASAP Capital.
                        </p>
                        <p>
                          Accessing the Site, in any manner, whether automated or otherwise, constitutes use of the Site and your agreement to be bound by these Terms of Service.
                        </p>
                        <p>
                          We reserve the right to change these Terms of Service or to impose new conditions on the use of the Site from time to time, in which case we will post the revised Terms of Service on this website. By continuing to use the Site after we post any such changes, you accept the Terms of Service, as modified.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* 4. User Eligibility */}
                  <section id="user-eligibility" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <Users className="h-6 w-6 text-[#D4AF37]" />
                        4. User Eligibility
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          To use our Website or services, you must be at least 18 years old or have the legal capacity to enter into a binding contract. If you are under 18, you may not use our services without the consent of a parent or guardian.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* 5. Intellectual Property Rights */}
                  <section id="intellectual-property" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <Copyright className="h-6 w-6 text-[#D4AF37]" />
                        5. Intellectual Property Rights
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-6">
                        {/* Ownership */}
                        <div>
                          <h3 className="text-lg font-semibold text-white mb-2">Ownership</h3>
                          <p>
                            All content on this Site, including but not limited to text, graphics, logos, images, videos, documents, branding, and software, is the property of ASAP Capital or its licensors and is protected by applicable intellectual property laws.
                          </p>
                        </div>

                        {/* Limited License */}
                        <div>
                          <h3 className="text-lg font-semibold text-white mb-2">Limited License</h3>
                          <p className="mb-3">The Site is provided solely for your personal, non-commercial use. You may not:</p>
                          <ul className="list-disc list-inside space-y-1 ml-2">
                            <li>Copy, reproduce, republish, upload, post, transmit, or distribute any content</li>
                            <li>Modify or create derivative works</li>
                            <li>Sell, license, or exploit content for commercial purposes</li>
                          </ul>
                          <p className="mt-3">
                            You may download or print one copy of individual pages for personal, non-commercial use, provided all copyright and proprietary notices remain intact.
                          </p>
                        </div>

                        {/* User-Submitted Content */}
                        <div>
                          <h3 className="text-lg font-semibold text-white mb-2">User-Submitted Content</h3>
                          <p>
                            By submitting content to ASAP Capital (including comments, testimonials, media, or communications), you represent that you have the legal right to do so. You grant ASAP Capital a royalty-free, perpetual, irrevocable, non-exclusive, worldwide license to use, reproduce, modify, distribute, and display such content in connection with our business operations.
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  </section>

                  {/* 6. Disclaimers */}
                  <section id="disclaimers" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <AlertTriangle className="h-6 w-6 text-[#D4AF37]" />
                        6. Disclaimers
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          The Site and all information, services, and materials are provided "as is" and without warranties of any kind, either express or implied.
                        </p>
                        <p className="mb-2">To the fullest extent permitted by law, ASAP Capital disclaims all warranties, including but not limited to:</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li>Implied warranties of merchantability</li>
                          <li>Fitness for a particular purpose</li>
                          <li>Non-infringement</li>
                        </ul>
                        <p className="mt-4">
                          We do not guarantee that the Site will be uninterrupted, error-free, or secure.
                        </p>
                        <p>
                          You agree to indemnify and hold harmless ASAP Capital, its officers, directors, employees, and affiliates from any claims, damages, liabilities, costs, or expenses arising from your violation of these Terms.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* 7. Third-Party Links */}
                  <section id="third-party-links" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <ExternalLink className="h-6 w-6 text-[#D4AF37]" />
                        7. Third-Party Links
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          The Site may contain links to third-party websites. These links are provided for convenience only.
                        </p>
                        <p>
                          ASAP Capital does not endorse and is not responsible for the content, policies, or practices of any third-party websites. Your use of such websites is at your own risk.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* 8. Online Commerce */}
                  <section id="online-commerce" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <Globe className="h-6 w-6 text-[#D4AF37]" />
                        8. Online Commerce
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          Certain sections of the Site may allow you to engage with third-party vendors or services.
                        </p>
                        <p>
                          ASAP Capital is not responsible for the quality, accuracy, reliability, or performance of products or services offered by third parties.
                        </p>
                        <p>
                          Any transaction or interaction with third parties is solely between you and that third party.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* 9. Account Registration & Security */}
                  <section id="account-security" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <Lock className="h-6 w-6 text-[#D4AF37]" />
                        9. Account Registration & Security
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p className="mb-2">If you create an account on the Site, you agree to:</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li>Provide accurate, current, and complete information</li>
                          <li>Maintain the confidentiality of your login credentials</li>
                          <li>Be responsible for all activity under your account</li>
                        </ul>
                        <p className="mt-4">
                          If you suspect unauthorized use of your account, notify us immediately at:{' '}
                          <a href="mailto:info@asapcapital.net" className="text-[#D4AF37] hover:underline">info@asapcapital.net</a>
                        </p>
                        <p>
                          ASAP Capital is not liable for losses resulting from failure to safeguard your account credentials.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* 10. Termination */}
                  <section id="termination" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <Ban className="h-6 w-6 text-[#D4AF37]" />
                        10. Termination
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p className="mb-2">We reserve the right to suspend or terminate your access to the Site, without notice, if:</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li>You violate these Terms</li>
                          <li>You engage in unlawful conduct</li>
                          <li>We determine your use is harmful to our business or users</li>
                        </ul>
                        <p className="mt-4">
                          Upon termination, you must immediately cease use of the Site and any content obtained from it.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* 10. Governing Law */}
                  <section id="governing-law" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <Gavel className="h-6 w-6 text-[#D4AF37]" />
                        11. Governing Law
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          These Terms shall be governed by and construed in accordance with the laws of the State of Texas.
                        </p>
                        <p>
                          Any disputes arising under these Terms shall be resolved in the appropriate courts located in Texas, unless otherwise required by applicable law.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* 12. Changes to These Terms */}
                  <section id="changes" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <RefreshCw className="h-6 w-6 text-[#D4AF37]" />
                        12. Changes to These Terms
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          We may update these Terms from time to time. The most current version will always be available on our Website with the updated effective date.
                        </p>
                        <p>
                          Your continued use of the Site after changes are posted constitutes acceptance of the revised Terms.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* 13. Contact Information */}
                  <section id="contact" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <Phone className="h-6 w-6 text-[#D4AF37]" />
                        13. Contact Information
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>If you have questions regarding these Terms of Service, please contact:</p>
                        <div className="bg-white/5 rounded-xl p-6 border border-white/10 space-y-3">
                          <p className="text-lg font-semibold text-white">ASAP Capital</p>
                          <p>
                            <span className="text-white/40">Phone:</span>{' '}
                            <a href="tel:+13463100995" className="text-[#D4AF37] hover:underline">+1 (346) 310-0995</a>
                          </p>
                          <p>
                            <span className="text-white/40">Email:</span>{' '}
                            <a href="mailto:info@asapcapital.net" className="text-[#D4AF37] hover:underline">info@asapcapital.net</a>
                          </p>
                          <p>
                            <span className="text-white/40">Website:</span>{' '}
                            <a 
                              href="https://asapcapital.net" 
                              className="text-[#D4AF37] hover:underline"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              https://asapcapital.net
                            </a>
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  </section>

                  {/* Final Statement */}
                  <section className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <div className="bg-[#D4AF37]/10 rounded-xl p-6 border border-[#D4AF37]/20">
                        <p className="text-white/80 text-center font-medium">
                          By using our Website and services, you acknowledge and agree to these Terms of Service.
                        </p>
                      </div>
                    </motion.div>
                  </section>
                </div>
              </div>
            </div>
          </section>

          {/* ==================== RELATED LINKS ==================== */}
          <section className="py-8 relative">
            <div className="max-w-[900px] mx-auto px-6 lg:px-12">
              <div className="flex flex-wrap justify-center gap-4">
                <a 
                  href="https://asapcapital.net/privacy"
                  className="px-5 py-2 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm hover:border-[#D4AF37]/30 hover:text-[#D4AF37] transition-all"
                >
                  Privacy Policy
                </a>
                <Link 
                  to="/disclosures"
                  className="px-5 py-2 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm hover:border-[#D4AF37]/30 hover:text-[#D4AF37] transition-all"
                >
                  Disclosures
                </Link>
                <Link 
                  to="/contact"
                  className="px-5 py-2 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm hover:border-[#D4AF37]/30 hover:text-[#D4AF37] transition-all"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default TermsPage;
